/**
 * Screener single-question card.
 *
 * Renders one question at a time with:
 *   - Linear progress bar (driven by `screenerProgressFraction`)
 *   - Optional "back" button (advances handled by the parent route)
 *   - Boolean / single-select / multi-select inputs styled as
 *     full-width tap targets so the layout works on mobile
 *
 * The card is purely presentational: parent owns `answers` and the
 * `onAnswer` / `onBack` handlers. Routing logic lives in the
 * router; navigation lives in the page wrapper.
 */
import styles from './Screener.module.css';
import { type IScreenerQuestion } from './screenerConfig';
import {
  screenerProgressFraction,
  type IScreenerAnswers,
} from './screenerRouter';
import { screenerQuestions } from './screenerConfig';
import { ScreenerTopBar } from './ScreenerTopBar';

export interface IScreenerQuestionCardProps {
  question: IScreenerQuestion;
  answers: IScreenerAnswers;
  /** Fires the moment the user picks an answer (or the user clicks Next for multi-select). */
  onAnswer: (
    questionId: string,
    value: boolean | string | readonly string[],
  ) => void;
  /** Fires when the user clicks the back button. Hidden for the first question. */
  onBack?: () => void;
  /**
   * Index in the screener-question array. Used purely for the
   * "Question N of 22" display.
   */
  index: number;
  /** Persistent "Sign in" affordance in the top bar — hidden when
   *  omitted. */
  onSignInClick?: () => void;
}

export function ScreenerQuestion({
  question,
  answers,
  onAnswer,
  onBack,
  index,
  onSignInClick,
}: IScreenerQuestionCardProps): JSX.Element {
  const total = screenerQuestions.length;
  const fraction = Math.round(screenerProgressFraction(answers) * 100);
  const currentValue = (answers as Record<string, unknown>)[question.id];

  return (
    <main
      id="main-content"
      className={styles.screener}
      aria-labelledby="screener-q-title"
    >
      <ScreenerTopBar
        metaLabel="Pre-qualification screener"
        onSignInClick={onSignInClick}
      />
      <div className={styles.screener__container}>
        <div className={styles.screener__question_progress}>
          <div className={styles.screener__question_progress_label}>
            <span>
              Question {index + 1} of {total}
            </span>
            <span>{fraction}% complete</span>
          </div>
          <div
            className={styles.screener__question_progress_track}
            role="progressbar"
            aria-valuenow={fraction}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Screener progress"
          >
            <div
              className={styles.screener__question_progress_bar}
              style={{ width: `${fraction}%` }}
            />
          </div>
        </div>

        <section
          className={styles.screener__question_card}
          aria-labelledby="screener-q-title"
        >
          {question.topicHint ? (
            <div className={styles.screener__question_kicker}>
              {question.topicHint}
            </div>
          ) : null}
          <h1 id="screener-q-title" className={styles.screener__question_title}>
            {question.displayCopy}
          </h1>
          {question.helpText ? (
            <p className={styles.screener__question_help}>
              {question.helpText}
            </p>
          ) : null}

          {question.kind === 'boolean' ? (
            <BooleanInput
              questionId={question.id}
              value={typeof currentValue === 'boolean' ? currentValue : undefined}
              onChange={next => onAnswer(question.id, next)}
            />
          ) : null}

          {question.kind === 'single_select' ? (
            <SingleSelectInput
              questionId={question.id}
              options={question.options ?? []}
              value={typeof currentValue === 'string' ? currentValue : ''}
              onChange={next => onAnswer(question.id, next)}
            />
          ) : null}

          {question.kind === 'multi_select' ? (
            <MultiSelectInput
              questionId={question.id}
              options={question.options ?? []}
              value={Array.isArray(currentValue) ? (currentValue as readonly string[]) : []}
              onChange={next => onAnswer(question.id, next)}
            />
          ) : null}

          <div className={styles.screener__nav}>
            {onBack ? (
              <button
                type="button"
                className={styles.screener__back_button}
                onClick={onBack}
              >
                ← Back
              </button>
            ) : (
              <span />
            )}
            {/* The boolean and single-select inputs auto-advance when
             * the user picks an answer (handled by the page wrapper).
             * Multi-select shows an explicit Next button because the
             * user typically chooses several options before
             * advancing. */}
            {question.kind === 'multi_select' ? (
              <button
                type="button"
                className={styles.screener__next_button}
                onClick={() => {
                  // No-op if no value picked — disabled below.
                  if (
                    Array.isArray(currentValue) &&
                    (currentValue as readonly string[]).length > 0
                  ) {
                    onAnswer(question.id, currentValue as readonly string[]);
                  }
                }}
                disabled={
                  !Array.isArray(currentValue) ||
                  (currentValue as readonly string[]).length === 0
                }
              >
                Continue →
              </button>
            ) : null}
          </div>
        </section>
      </div>
    </main>
  );
}

function BooleanInput({
  questionId,
  value,
  onChange,
}: {
  questionId: string;
  value: boolean | undefined;
  onChange: (next: boolean) => void;
}): JSX.Element {
  // See SingleSelectInput for the onClick + keyboard nav rationale.
  const opts = [true, false];
  const handleKey = (
    ev: React.KeyboardEvent<HTMLLIElement>,
    optValue: boolean,
  ): void => {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      onChange(optValue);
      return;
    }
    if (
      ev.key === 'ArrowDown' ||
      ev.key === 'ArrowRight' ||
      ev.key === 'ArrowUp' ||
      ev.key === 'ArrowLeft'
    ) {
      ev.preventDefault();
      const sibling =
        ev.key === 'ArrowDown' || ev.key === 'ArrowRight'
          ? (ev.currentTarget.nextElementSibling as HTMLLIElement | null) ??
            (ev.currentTarget.parentElement?.firstElementChild as HTMLLIElement | null)
          : (ev.currentTarget.previousElementSibling as HTMLLIElement | null) ??
            (ev.currentTarget.parentElement?.lastElementChild as HTMLLIElement | null);
      sibling?.focus();
    }
  };
  return (
    <ul className={styles.screener__option_list} role="radiogroup">
      {opts.map((opt, index) => {
        const id = `${questionId}-${opt ? 'yes' : 'no'}`;
        const selected = value === opt;
        return (
          <li
            key={id}
            className={
              selected
                ? `${styles.screener__option} ${styles['screener__option--selected']}`
                : styles.screener__option
            }
            role="radio"
            aria-checked={selected}
            tabIndex={selected || (value === undefined && index === 0) ? 0 : -1}
            onKeyDown={ev => handleKey(ev, opt)}
            onClick={() => onChange(opt)}
          >
            <span style={{ cursor: 'pointer', display: 'flex', width: '100%' }}>
              {/* Decorative radio dot — pure CSS via the styles
                  module. Not an <input> because the parent <li
                  role="radio"> is the interactive element; nesting
                  a real <input> here would trip
                  axe:nested-interactive. The `data-checked` attr
                  drives the visual state. */}
              <span
                aria-hidden="true"
                data-checked={selected ? 'true' : 'false'}
                className={styles.screener__option_input}
              />
              <span className={styles.screener__option_text}>
                <span className={styles.screener__option_label}>
                  {opt ? 'Yes' : 'No'}
                </span>
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function SingleSelectInput({
  questionId,
  options,
  value,
  onChange,
}: {
  questionId: string;
  options: readonly { value: string; label: string; description?: string }[];
  value: string;
  onChange: (next: string) => void;
}): JSX.Element {
  // Use onClick on the <li> rather than the native radio's `change`
  // event so that re-clicking an already-selected option still
  // triggers `onChange`. The browser suppresses `change` when the
  // input's checked state doesn't transition — which means a user
  // returning to a pre-answered question via Back has no way to
  // proceed (the radio looks checked, no Continue button is shown
  // because single-select is supposed to auto-advance). Forcing the
  // callback on every click makes "click the same answer again"
  // a valid way to re-confirm and advance.
  //
  // Keyboard parity. The <li> gets
  // tabIndex=0 and Enter/Space firing onChange so a keyboard user
  // can advance the screener without ever touching the mouse. Arrow
  // keys (↑/↓) move focus between options within the group, matching
  // the WAI-ARIA radiogroup pattern.
  const handleKey = (
    ev: React.KeyboardEvent<HTMLLIElement>,
    optValue: string,
  ): void => {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      onChange(optValue);
      return;
    }
    if (ev.key === 'ArrowDown' || ev.key === 'ArrowRight') {
      ev.preventDefault();
      const next = (ev.currentTarget.nextElementSibling as HTMLLIElement | null)
        ?? (ev.currentTarget.parentElement?.firstElementChild as HTMLLIElement | null);
      next?.focus();
    }
    if (ev.key === 'ArrowUp' || ev.key === 'ArrowLeft') {
      ev.preventDefault();
      const prev = (ev.currentTarget.previousElementSibling as HTMLLIElement | null)
        ?? (ev.currentTarget.parentElement?.lastElementChild as HTMLLIElement | null);
      prev?.focus();
    }
  };
  return (
    <ul className={styles.screener__option_list} role="radiogroup">
      {options.map((opt, index) => {
        const id = `${questionId}-${opt.value}`;
        const selected = value === opt.value;
        return (
          <li
            key={id}
            className={
              selected
                ? `${styles.screener__option} ${styles['screener__option--selected']}`
                : styles.screener__option
            }
            role="radio"
            aria-checked={selected}
            tabIndex={selected || (value === '' && index === 0) ? 0 : -1}
            onKeyDown={ev => handleKey(ev, opt.value)}
            onClick={() => onChange(opt.value)}
          >
            <span style={{ cursor: 'pointer', display: 'flex', width: '100%' }}>
              {/* Decorative radio dot — pure CSS via the styles
                  module. Not an <input> because the parent <li
                  role="radio"> is the interactive element; nesting
                  a real <input> here would trip
                  axe:nested-interactive. The `data-checked` attr
                  drives the visual state. */}
              <span
                aria-hidden="true"
                data-checked={selected ? 'true' : 'false'}
                className={styles.screener__option_input}
              />
              <span className={styles.screener__option_text}>
                <span className={styles.screener__option_label}>{opt.label}</span>
                {opt.description ? (
                  <span className={styles.screener__option_description}>
                    {opt.description}
                  </span>
                ) : null}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function MultiSelectInput({
  questionId,
  options,
  value,
  onChange,
}: {
  questionId: string;
  options: readonly { value: string; label: string; description?: string }[];
  value: readonly string[];
  onChange: (next: readonly string[]) => void;
}): JSX.Element {
  const selectedSet = new Set(value);
  const toggle = (v: string): void => {
    const next = new Set(selectedSet);
    if (next.has(v)) {
      next.delete(v);
    } else {
      // "none" is mutually exclusive with the harm options.
      if (v === 'none') {
        next.clear();
      } else {
        next.delete('none');
      }
      next.add(v);
    }
    onChange(Array.from(next));
  };

  return (
    <ul className={styles.screener__option_list}>
      {options.map(opt => {
        const id = `${questionId}-${opt.value}`;
        const selected = selectedSet.has(opt.value);
        return (
          <li
            key={id}
            className={
              selected
                ? `${styles.screener__option} ${styles['screener__option--selected']}`
                : styles.screener__option
            }
          >
            <label htmlFor={id} style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                id={id}
                className={styles.screener__option_input}
                checked={selected}
                onChange={() => toggle(opt.value)}
              />
              <span className={styles.screener__option_text}>
                <span className={styles.screener__option_label}>{opt.label}</span>
                {opt.description ? (
                  <span className={styles.screener__option_description}>
                    {opt.description}
                  </span>
                ) : null}
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}
